import React, { useState } from 'react';
import {
  getBrisbaneEventDate,
  formatDisplayDate
} from '../utils/eventDisplayUtils';
import { groupRowsByBrisbaneDate } from '../utils/eventTableUtils';
import EventRow from './EventRow';

// EventTable displays a list of events grouped by Brisbane date.
// It manages expansion state for each event's directions/details.
const EventTable = ({ rows, searchInitiated, showNotification }) => {
  // Track which event rows have expanded directions/details
  const [expandedDirections, setExpandedDirections] = useState({});

  // Helper to trim "Australia" from address and whitespace
  const getTrimmedAddress = (address) => {
    if (!address) return '';
    return address.replace(/,\s*australia\s*$/i, '').trim();
  };

  // Copy text to clipboard and show a notification
  const copyToClipboard = (text) => {
    const trimmed = getTrimmedAddress(text);
    showNotification(`Copied: ${trimmed}`, 'success');
    navigator.clipboard.writeText(trimmed).catch(err => {
      console.error('Failed to copy text: ', err);
      showNotification('Failed to copy', 'error');
    });
  };

  // Group events by their Brisbane date for display
  const groupedRows = groupRowsByBrisbaneDate(
    rows,
    getBrisbaneEventDate,
    formatDisplayDate
  );

  // Show message if no events match filters
  if (!rows || rows.length === 0) {
    return (
      <p className="text-center text-gray-500 mt-4">
        No events match the current filters.
      </p>
    );
  }

  // Render grouped events in a table, with expandable rows
  return (
    <div className="w-full max-w-[1200px] mx-auto">
      <div className="rounded-b-lg overflow-hidden bg-white">
        <div className="overflow-y-auto max-h-[550px]">
          <table className="w-full border-collapse relative table-fixed">
            <tbody>
              {Object.entries(groupedRows).map(([date, events]) => (
                <React.Fragment key={date}>
                  {/* Date Header Row */}
                  <tr className="sticky top-0 z-10">
                    <td
                      colSpan="1"
                      className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 font-bold px-2 py-1 border border-gray-200 shadow-sm sticky top-0 z-10"
                      style={{ backgroundClip: 'padding-box' }}
                    >
                      <div className="text-left text-base italic pl-2">{date}</div>
                    </td>
                  </tr>
                  {/* Event Rows for this date */}
                  {events.map((row, index) => {
                    const eventKey = `${date}_${index}`;
                    const isExpanded = expandedDirections[eventKey];
                    return (
                      <EventRow
                        key={index}
                        row={row}
                        eventKey={eventKey}
                        isExpanded={isExpanded}
                        onToggleExpand={() =>
                          setExpandedDirections(prevState => ({
                            ...prevState,
                            [eventKey]: !prevState[eventKey]
                          }))
                        }
                        copyToClipboard={copyToClipboard}
                      />
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EventTable;